import { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";
import { EPOCH_START, formatYear, posFromYear, yearFromPos } from "@/data/enhancements";
import Leaderboard from "@/components/Leaderboard";

const EASE = [0.22, 1, 0.36, 1];

export default function SiteIndex({ sites, activeId, onSelect, epochYear, onEpochChange, routeIds, onToggleRoute, compareIds, onToggleCompare, notedIds }) {
  const [filter, setFilter] = useState("All");
  const [preview, setPreview] = useState(null);
  const listRef = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 180, damping: 22 });
  const sy = useSpring(my, { stiffness: 180, damping: 22 });

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(sites.map((s) => s.category)))],
    [sites]
  );
  const visible = useMemo(
    () =>
      sites.filter(
        (s) =>
          (filter === "All" || s.category === filter) &&
          (epochYear == null || (EPOCH_START[s.id] ?? 0) >= epochYear)
      ),
    [sites, filter, epochYear]
  );
  const epochCount = useMemo(
    () =>
      epochYear == null
        ? sites.length
        : sites.filter((s) => (EPOCH_START[s.id] ?? 0) >= epochYear).length,
    [sites, epochYear]
  );

  return (
    <section
      id="index"
      data-testid="index-section"
      className="pointer-events-none relative z-10 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-[1680px] px-5 lg:px-16">
        <div className="pointer-events-auto max-w-[780px]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-6 font-mono text-[10px] tracking-[0.4em] text-[#D4AF37]"
          >
            THE INDEX — 02
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.08, ease: EASE }}
            className="font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl"
          >
            Fifteen <em className="italic text-[#D4AF37]">open questions.</em>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.18, ease: EASE }}
            className="mt-8 flex flex-wrap gap-2"
          >
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`rounded-full border px-4 py-2 font-mono text-[9px] tracking-[0.22em] transition-all duration-300 ${
                  filter === c
                    ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B0C10]"
                    : "border-white/20 text-white/55 hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
                }`}
              >
                {c.toUpperCase()}
              </button>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8, delay: 0.24, ease: EASE }}
            data-testid="epoch-scrubber"
            className="mt-6 border border-[#38BDF8]/25 bg-[#12141C]/85 p-5 backdrop-blur"
          >
            <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-[9px] tracking-[0.28em] text-white/50">
              <span>
                EPOCH VIEW — ORIGIN ≥{" "}
                <span data-testid="epoch-year" className="text-[#38BDF8]">
                  {formatYear(epochYear ?? -16000)}
                </span>
              </span>
              <span className="text-white/40" data-testid="epoch-count">
                {epochCount}/{sites.length} BEACONS
              </span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={0.5}
              value={posFromYear(epochYear ?? -16000)}
              onChange={(e) => onEpochChange(yearFromPos(Number(e.target.value)))}
              data-testid="epoch-slider"
              aria-label="Epoch scrubber"
              className="epoch-slider mt-4 w-full"
            />
            <div className="mt-1.5 flex justify-between font-mono text-[8px] tracking-[0.2em] text-white/30">
              <span>16,000 BCE</span>
              <span>2000 CE</span>
            </div>
            {epochYear !== null && (
              <button
                data-testid="epoch-reset"
                onClick={() => onEpochChange(null)}
                className="mt-3 font-mono text-[9px] tracking-[0.3em] text-[#FF9F1C] transition-colors hover:text-[#D4AF37]"
              >
                ← SHOW ALL ERAS
              </button>
            )}
          </motion.div>

          <Leaderboard sites={sites} onSelect={onSelect} refreshKey={activeId} />
        </div>

        <div
          ref={listRef}
          className="pointer-events-auto mt-12 max-w-[860px] border-t border-[#D4AF37]/15"
          onMouseMove={(e) => {
            mx.set(e.clientX + 28);
            my.set(e.clientY - 90);
          }}
          onMouseLeave={() => setPreview(null)}
        >
          {visible.map((site, i) => (
            <motion.div
              key={site.id}
              data-testid={`site-row-${site.id}`}
              role="button"
              tabIndex={0}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: Math.min(i * 0.05, 0.4), ease: EASE }}
              onClick={() => onSelect(site)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSelect(site);
              }}
              onMouseEnter={() => setPreview(site)}
              className={`group relative flex w-full cursor-pointer items-center gap-6 border-b border-[#D4AF37]/15 py-6 text-left transition-colors duration-300 lg:gap-10 ${
                activeId === site.id ? "bg-[#D4AF37]/[0.06]" : "hover:bg-white/[0.03]"
              }`}
            >
              {activeId === site.id && (
                <span className="absolute left-0 top-0 h-full w-0.5 bg-[#D4AF37]" />
              )}
              <span className="w-8 shrink-0 self-start font-mono text-[11px] text-[#D4AF37]/70">
                {String(sites.indexOf(site) + 1).padStart(2, "0")}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate font-serif text-2xl leading-tight transition-colors duration-300 sm:text-3xl ${
                    activeId === site.id ? "text-[#D4AF37]" : "text-[#F8FAFC] group-hover:text-[#D4AF37]"
                  }`}
                >
                  {site.name}
                </span>
                <span className="mt-1.5 block font-mono text-[10px] tracking-[0.18em] text-white/40">
                  {site.location.toUpperCase()} · {site.lat.toFixed(2)}°{site.lat >= 0 ? "N" : "S"}{" "}
                  {Math.abs(site.lng).toFixed(2)}°{site.lng >= 0 ? "E" : "W"}
                  {notedIds?.has(site.id) && (
                    <span data-testid={`note-indicator-${site.id}`} className="ml-3 text-[#D4AF37]/80">
                      ✎ NOTED
                    </span>
                  )}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3 md:gap-5">
                <span className="hidden font-mono text-[9px] tracking-[0.22em] text-white/35 md:block">
                  {site.category.toUpperCase()}
                </span>
                <button
                  data-testid={`compare-toggle-${site.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCompare(site.id);
                  }}
                  aria-label={
                    compareIds.includes(site.id)
                      ? `Unpin ${site.name} from comparison`
                      : `Pin ${site.name} for comparison`
                  }
                  title="Compare"
                  className={`flex h-9 w-9 items-center justify-center rounded-full border font-serif text-sm leading-none transition-all duration-300 ${
                    compareIds.includes(site.id)
                      ? "border-[#38BDF8] bg-[#38BDF8] text-[#0B0C10]"
                      : "border-white/25 text-white/60 hover:border-[#38BDF8] hover:text-[#38BDF8]"
                  }`}
                >
                  ⇄
                </button>
                <button
                  data-testid={`route-toggle-${site.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleRoute(site.id);
                  }}
                  aria-label={
                    routeIds.includes(site.id)
                      ? `Remove ${site.name} from route`
                      : `Add ${site.name} to route`
                  }
                  className={`flex h-9 w-9 items-center justify-center rounded-full border font-serif text-base leading-none transition-all duration-300 ${
                    routeIds.includes(site.id)
                      ? "border-[#D4AF37] bg-[#D4AF37] text-[#0B0C10]"
                      : "border-white/25 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                  }`}
                >
                  {routeIds.includes(site.id) ? "★" : "+"}
                </button>
                <span className="hidden font-serif text-2xl text-[#D4AF37] opacity-0 transition-all duration-300 group-hover:translate-x-1.5 group-hover:opacity-100 md:block">
                  →
                </span>
              </span>
            </motion.div>
          ))}
          {visible.length === 0 && (
            <div className="border-b border-[#D4AF37]/15 py-10 font-mono text-[11px] tracking-[0.2em] text-white/40">
              NO SITES IN THIS CATEGORY.
            </div>
          )}
        </div>
      </div>

      {/* floating hover preview */}
      <AnimatePresence>
        {preview && (
          <motion.div
            key={preview.id}
            data-testid="index-hover-preview"
            initial={{ opacity: 0, scale: 0.92, rotate: -3 }}
            animate={{ opacity: 1, scale: 1, rotate: 2 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.25, ease: EASE }}
            style={{ x: sx, y: sy }}
            className="pointer-events-none fixed left-0 top-0 z-40 hidden lg:block"
          >
            <div className="h-44 w-64 overflow-hidden border border-[#D4AF37]/40 shadow-2xl shadow-black/60">
              <img
                src={preview.image}
                alt={preview.name}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-2 pt-6 font-mono text-[8px] tracking-[0.3em] text-[#D4AF37]">
                {preview.age.toUpperCase()}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
